// @ts-nocheck
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface CoachProspectFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const DIVISION_LEVELS = ["D1", "D2", "D3", "NAIA", "JUCO"];
const SPORTS = [
  "Football", "Basketball", "Baseball", "Soccer", "Track & Field", 
  "Wrestling", "Swimming", "Tennis", "Golf", "Softball", "Volleyball", "Other"
];
const BUDGET_RANGES = [
  "$0-500", "$500-1000", "$1000-2500", "$2500-5000", "$5000-10000", "$10000+"
];
const LEAD_SOURCES = [
  "Website", "Referral", "Conference", "Social Media", "Email Campaign", "Cold Outreach", "Partner"
];

export const CoachProspectForm: React.FC<CoachProspectFormProps> = ({ onClose, onSuccess }) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    coach_name: "",
    college_name: "",
    sport: "",
    division_level: "",
    email: "",
    phone: "",
    recruiting_budget_range: "",
    lead_source: "",
    estimated_subscription_value: "",
    notes: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase.from("coach_prospects").insert({
        ...formData,
        sales_rep_id: profile.id,
        estimated_subscription_value: formData.estimated_subscription_value 
          ? parseInt(formData.estimated_subscription_value) 
          : null,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Coach prospect added successfully",
      });
      onSuccess();
    } catch (error) {
      console.error("Error creating coach prospect:", error);
      toast({
        title: "Error",
        description: "Failed to add coach prospect",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Add New Coach Prospect</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coach_name">Coach Name *</Label>
                <Input
                  id="coach_name"
                  value={formData.coach_name}
                  onChange={(e) => handleChange("coach_name", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="college_name">College Name *</Label>
                <Input
                  id="college_name"
                  value={formData.college_name}
                  onChange={(e) => handleChange("college_name", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sport">Sport *</Label>
                <Select value={formData.sport} onValueChange={(value) => handleChange("sport", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPORTS.map((sport) => (
                      <SelectItem key={sport} value={sport}>
                        {sport}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="division_level">Division Level</Label>
                <Select value={formData.division_level} onValueChange={(value) => handleChange("division_level", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select division" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIVISION_LEVELS.map((division) => (
                      <SelectItem key={division} value={division}>
                        {division}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="recruiting_budget_range">Recruiting Budget Range</Label>
                <Select value={formData.recruiting_budget_range} onValueChange={(value) => handleChange("recruiting_budget_range", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_RANGES.map((range) => (
                      <SelectItem key={range} value={range}>
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="lead_source">Lead Source</Label>
                <Select value={formData.lead_source} onValueChange={(value) => handleChange("lead_source", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map((source) => (
                      <SelectItem key={source} value={source}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="estimated_subscription_value">Estimated Monthly Subscription Value ($)</Label>
              <Input
                id="estimated_subscription_value"
                type="number"
                value={formData.estimated_subscription_value}
                onChange={(e) => handleChange("estimated_subscription_value", e.target.value)}
                placeholder="e.g. 99"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Additional notes about this coach prospect..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Prospect"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};